import { Column } from '../enum/Column';
import { StatsGroupEnum } from '../enum/StatsGroupEnum';

/**
 * Resolves the active geo grouping level from the current column visibility state.
 *
 * The function determines the deepest visible geo column from the provided ordered
 * geo hierarchy. The deepest visible geo level represents the current geo grouping
 * selection used by the table state resolution logic.
 *
 * Important:
 * - This function returns a single numeric geo group level.
 * - It does NOT build the final API `groupBy` payload.
 * - The returned value is later used as an input for
 *   `useResolvedTableState` / `resolveGroupBy` to calculate the final grouping.
 *
 * Visibility handling:
 * - Uses `pendingVisible` when available, otherwise falls back to `isVisible`.
 * - Iterates through the geo hierarchy from the deepest level upwards.
 * - Returns the first geo column that exists and is currently visible.
 *
 * @param geoColumnsOrdered - Ordered list of geo columns following the hierarchy
 *                             from the highest level to the deepest level.
 * @param columns - Current table column configuration containing visibility state.
 *
 * @returns The numeric `StatsGroupEnum` value of the deepest visible geo level,
 *          or `undefined` when no visible geo column exists.
 */

export const resolveGroupByFromColumns = (geoColumnsOrdered: string[], columns: Column[] = []): number | undefined => {
  const visibilityMap = new Map(columns.map((c) => [c.name, c.pendingVisible ?? c.isVisible]));

  const deepestVisible = [...geoColumnsOrdered]
    .reverse()
    .find((name) => visibilityMap.get(name) !== false && visibilityMap.has(name));

  if (!deepestVisible) return undefined;

  const key = Object.keys(StatsGroupEnum).find((k: any) => StatsGroupEnum[k as any] === deepestVisible);

  return key !== undefined ? Number(key) : undefined;
};
